import {useMutation} from "@apollo/client";
import {useEffect} from "react";

import Button from "@/components/button/Button.tsx";
import {useCheckoutContext} from "@/components/context/hooks.ts";
import {GQL_USER_CONTEXT} from "@/lib/queries/accounts.ts";
import {GQL_CHECKOUT_CONTEXT, GQL_SET_PAYMENT_METHOD, SetPaymentMethodResponse} from "@/lib/queries/billing.ts";
import {BillingData, PaymentMethodType} from "@/lib/types/billing.ts";
import {PaymentMethodForm, PaymentMethodFormValues} from "@/pages/billing/payment-method/PaymentMethodForm.tsx";

export interface PaymentMethodEditProps {
  billingData: BillingData;
  braintreeToken: string;
  onCancel: () => void;
  onSuccess: () => void;
  withButtons?: boolean;
  withCancelButton: boolean;
  formId?: string;
  onTabChange?: (tab: PaymentMethodType) => void;
  subscription?: boolean;
}
export const PaymentMethodEdit = ({
  billingData,
  braintreeToken,
  onCancel,
  onSuccess,
  withButtons,
  withCancelButton,
  formId,
  onTabChange,
  subscription,
}: PaymentMethodEditProps) => {
  const [updatePaymentMethod, {data}] = useMutation<SetPaymentMethodResponse>(GQL_SET_PAYMENT_METHOD, {
    refetchQueries: [GQL_CHECKOUT_CONTEXT, GQL_USER_CONTEXT],
  });

  const serverErrors = data?.setPaymentMethod?.errors;
  const {setRequestInProgress, requestInProgress} = useCheckoutContext();
  const savePaymentMethod = (
    formValues: PaymentMethodFormValues,
    nonce: string,
    paymentMethodType: PaymentMethodType
  ) => {
    updatePaymentMethod({
      variables: {
        paymentMethodData: {
          payment_method: paymentMethodType,
          payment_method_nonce: nonce,
          ...formValues,
          subscription: !!subscription,
        },
      },
    }).then((res) => {
      if (res.data?.setPaymentMethod?.errors) {
        setRequestInProgress(false);
        if (res.data.setPaymentMethod.errors.__all__) {
          const errorMsgElement = document.getElementById("payment-processor-errors");
          errorMsgElement?.scrollIntoView({block: "end"});
        }
        return;
      }
      onSuccess();
    });
  };
  useEffect(() => {
    if (onTabChange) {
      onTabChange(PaymentMethodType.CREDIT_CARD);
    }
  }, [onTabChange]);
  return (
    <div className="row">
      <div className="col">
        <div className="display-block rounded mt-0 pt-1">
          <PaymentMethodForm
            braintreeToken={braintreeToken}
            billingData={billingData}
            onSave={savePaymentMethod}
            formId={formId || "paymentMethodForm"}
            serverErrors={serverErrors || {}}
            onTabChange={onTabChange}
          />
          {withButtons && (
            <div className="d-flex flex-row justify-content-end gap-2">
              {withCancelButton && (
                <Button variant={"secondary"} disabled={requestInProgress} onClick={() => onCancel()}>
                  Cancel
                </Button>
              )}
              <Button type="submit" disabled={requestInProgress} form={formId}>
                {requestInProgress ? "Saving..." : "Save"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
